import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { API_URL, getHeaders, Spinner, Empty, Pagination } from './shared';
import logger from '../../utils/logger';

export function AuditLog({ adminRole } = {}) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actions, setActions] = useState([]);
  const [filterAction, setFilterAction] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);
  const [digestPreviewOpen, setDigestPreviewOpen] = useState(false);
  const [digestPreviewLoading, setDigestPreviewLoading] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestSending, setDigestSending] = useState(false);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page) });
      if (filterAction) params.set('action', filterAction);
      if (dateFrom) params.set('date_from', new Date(dateFrom).toISOString());
      if (dateTo) params.set('date_to', new Date(dateTo + 'T23:59:59').toISOString());
      const res = await fetch(`${API_URL}/api/admin/audit-log?${params}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setEntries(data.entries || []);
      setTotalPages(data.total_pages);
      setTotal(data.total);
    } catch (err) { logger.error(err); toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  }, [page, filterAction, dateFrom, dateTo]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  // Actions list is loaded lazily on first dropdown focus — keeps it out of
  // the effect chain (avoids react-hooks/set-state-in-effect) and saves a
  // request when the operator never touches the filter.
  const loadActions = async () => {
    if (actions.length > 0) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/audit-log/actions`, { headers: getHeaders() });
      if (res.ok) setActions((await res.json()).actions || []);
    } catch (err) { logger.error('Audit log error:', err); }
  };

  const actionColors = {
    admin_login: 'bg-blue-600/20 text-blue-400',
    access_request_created: 'bg-yellow-600/20 text-yellow-400',
    access_request_approved: 'bg-green-600/20 text-green-400',
    access_request_denied: 'bg-red-600/20 text-red-400',
    identity_mapping_viewed: 'bg-purple-600/20 text-purple-400',
    role_created: 'bg-teal-600/20 text-teal-400',
    role_assigned: 'bg-orange-600/20 text-orange-400',
  };
  const formatAction = (a) => a?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';

  const handleExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterAction) params.set('action', filterAction);
      if (dateFrom) params.set('date_from', new Date(dateFrom).toISOString());
      if (dateTo) params.set('date_to', new Date(dateTo + 'T23:59:59').toISOString());
      const url = `${API_URL}/api/admin/audit-log/export${params.toString() ? `?${params}` : ''}`;
      const res = await fetch(url, { headers: getHeaders() });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const cap = parseInt(res.headers.get('X-Audit-Row-Cap') || '0', 10);
      const count = parseInt(res.headers.get('X-Audit-Row-Count') || '0', 10);
      const cd = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : `audit-log-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(dlUrl);
      if (cap && count >= cap) {
        toast.warning(`Exported ${count} rows (capped at ${cap}). Narrow your filters to capture older entries.`);
      } else {
        toast.success(`Exported ${count} audit ${count === 1 ? 'entry' : 'entries'}.`);
      }
    } catch (err) {
      logger.error('Audit export error:', err);
      toast.error('Failed to export audit log');
    } finally {
      setExporting(false);
    }
  };

  const handleDigestPreview = async () => {
    setDigestPreviewLoading(true);
    setDigestPreviewOpen(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/owner-digest/preview`, { headers: getHeaders() });
      if (!res.ok) throw new Error(`Preview failed (${res.status})`);
      setDigestPreview(await res.json());
    } catch (err) {
      logger.error('Digest preview error:', err);
      toast.error('Failed to load digest preview');
      setDigestPreviewOpen(false);
    } finally {
      setDigestPreviewLoading(false);
    }
  };

  const handleDigestSendNow = async () => {
    if (digestSending) return;
    if (!window.confirm('Send the Owner Weekly Digest now to the owner and every admin on file?')) return;
    setDigestSending(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/owner-digest/send-now`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`Send failed (${res.status})`);
      const body = await res.json();
      if (body.sent) {
        toast.success(`Digest dispatched to ${body.primary} + ${(body.cc || []).length} on CC.`);
      } else {
        toast.warning('Digest built but email provider unavailable. The send was audit-logged for review.');
      }
      setDigestPreviewOpen(false);
    } catch (err) {
      logger.error('Digest send error:', err);
      toast.error('Failed to send digest');
    } finally {
      setDigestSending(false);
    }
  };

  if (loading && entries.length === 0) return <Spinner text="Loading audit log..." />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Action</label>
          <select data-testid="audit-action-filter" value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }}
            onFocus={loadActions}
            className="text-xs bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-green-500">
            <option value="">All Actions</option>
            {actions.map(a => <option key={a} value={a}>{formatAction(a)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">From</label>
          <input data-testid="audit-date-from" type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
            className="text-xs bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-green-500" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">To</label>
          <input data-testid="audit-date-to" type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
            className="text-xs bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-green-500" />
        </div>
        {(filterAction || dateFrom || dateTo) && (
          <button onClick={() => { setFilterAction(''); setDateFrom(''); setDateTo(''); setPage(1); }}
            className="text-xs text-gray-400 hover:text-white px-3 py-2 transition">Clear Filters</button>
        )}
        <div className="ml-auto flex items-center gap-2">
          {adminRole === 'owner' && (
            <button
              data-testid="audit-preview-digest"
              onClick={handleDigestPreview}
              disabled={digestPreviewLoading}
              className="text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-emerald-500/60 text-white rounded-lg px-3 py-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Preview the Owner Weekly Digest that will be sent on Saturday 23:00 UTC"
            >
              {digestPreviewLoading ? 'Loading…' : 'Preview Weekly Digest'}
            </button>
          )}
          <button
            data-testid="audit-export-csv"
            onClick={handleExportCsv}
            disabled={exporting || total === 0}
            className="text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-green-500/60 text-white rounded-lg px-3 py-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
            title="Download current filtered view as CSV (capped at 10,000 rows)"
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500">{total} audit entries</p>
      {entries.length === 0 ? <Empty text="No audit entries match your filters" /> : (
        <div className="space-y-2">
          {entries.map((e, i) => (
            <div key={`${e.timestamp}-${i}`} data-testid={`audit-entry-${i}`} className="bg-gray-800 border border-gray-700 rounded-xl p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${actionColors[e.action] || 'bg-gray-700 text-gray-400'}`}>
                      {formatAction(e.action)}
                    </span>
                    {e.actor_name && <span className="text-sm text-white font-medium">{e.actor_name}</span>}
                    {e.actor_role && <span className="text-xs text-gray-500">({e.actor_role})</span>}
                  </div>
                  {e.details && Object.keys(e.details).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {Object.entries(e.details).map(([k, v]) => (
                        v && <span key={k} className="text-xs bg-gray-700/50 text-gray-400 px-2 py-0.5 rounded">
                          {k.replace(/_/g, ' ')}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <span className="text-xs text-gray-500 shrink-0">{e.timestamp ? new Date(e.timestamp).toLocaleString() : '-'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} label={`Page ${page} of ${totalPages} (${total} entries)`} />

      {digestPreviewOpen && (
        <div
          data-testid="digest-preview-modal"
          className="fixed inset-0 z-50 bg-black/70 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          onClick={() => !digestSending && setDigestPreviewOpen(false)}
        >
          <div
            className="bg-gray-900 border border-gray-700 rounded-xl max-w-3xl w-full my-8 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
              <div>
                <h3 className="text-base font-semibold text-white">Owner Weekly Digest — Preview</h3>
                <p className="text-xs text-gray-400 mt-0.5">Next automatic send: Saturday 23:00 UTC</p>
              </div>
              <button
                onClick={() => !digestSending && setDigestPreviewOpen(false)}
                className="text-gray-400 hover:text-white text-xl leading-none px-2"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {digestPreviewLoading || !digestPreview ? (
              <div className="px-5 py-12 text-center text-sm text-gray-400">Building preview…</div>
            ) : (
              <>
                <div className="px-5 py-3 border-b border-gray-800 text-xs text-gray-400 space-y-1">
                  <div><span className="text-gray-500">From:</span> <span className="text-white">{digestPreview.from || '—'}</span></div>
                  <div><span className="text-gray-500">Subject:</span> <span className="text-white">{digestPreview.subject}</span></div>
                  <div>
                    <span className="text-gray-500">To:</span>{' '}
                    <span className="text-white">{digestPreview.recipients?.[0] || '—'}</span>
                    {digestPreview.recipients && digestPreview.recipients.length > 1 && (
                      <>
                        <span className="text-gray-500">  ·  CC:</span>{' '}
                        <span className="text-white">{digestPreview.recipients.slice(1).join(', ')}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="bg-white text-black max-h-[55vh] overflow-y-auto" data-testid="digest-preview-html">
                  <div dangerouslySetInnerHTML={{ __html: digestPreview.html || '' }} />
                </div>
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-700">
                  <p className="text-xs text-gray-500">
                    Audit-logged as <code className="text-gray-300">owner_weekly_digest_sent</code>.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setDigestPreviewOpen(false)}
                      disabled={digestSending}
                      className="text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white rounded-lg px-3 py-2 transition disabled:opacity-50"
                    >
                      Close
                    </button>
                    <button
                      data-testid="audit-send-digest-now"
                      onClick={handleDigestSendNow}
                      disabled={digestSending}
                      className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg px-3 py-2 transition disabled:opacity-50"
                    >
                      {digestSending ? 'Sending…' : 'Send Now'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
